import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  ConfirmDialog,
  type ConfirmDialogOwnProps,
} from 'containers/ConfirmDialog/ConfirmDialog';
import {
  closeConfirmDialog as closeConfirmDialogAction,
  toggleConfirmDialog as toggleConfirmDialogAction,
} from 'containers/ConfirmDialog/actions';
import { selectorConfirmDialogDisplay } from 'containers/ConfirmDialog/selectors';
import { useEffect, useMemo } from 'react';
import type { ComponentType } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';

interface SelectorProps {
  showDialog: Map<string, boolean>;
}

interface Props {
  closeOnUnmount?: boolean;
  name: string;
}

type ConfirmDialogPropsWithoutId = Omit<ConfirmDialogOwnProps, 'id'>;

interface ConfirmDialogHook {
  closeConfirmDialog: () => void;
  DialogComponent: ComponentType<ConfirmDialogPropsWithoutId>;
  dialogOpen: boolean;
  toggleConfirmDialog: () => void;
}

const selector = createStructuredSelector({
  showDialog: selectorConfirmDialogDisplay(),
});

export const useConfirmDialog = (props: Props): ConfirmDialogHook => {
  const dispatch = useDispatch();
  const { showDialog } = useSelector<unknown, SelectorProps>(selector);

  const closeConfirmDialog = useStableCallback(() => {
    dispatch(closeConfirmDialogAction(props.name));
  });

  const toggleConfirmDialog = useStableCallback(() => {
    dispatch(toggleConfirmDialogAction(props.name));
  });

  const maybeCloseOnUnmount = useStableCallback(() => {
    if (props.closeOnUnmount && showDialog.get(props.name)) {
      closeConfirmDialog();
    }
  });

  useEffect(() => {
    return () => {
      maybeCloseOnUnmount();
    };
  }, []);

  const DialogComponent = useMemo(
    () => (componentProps: ConfirmDialogPropsWithoutId) => (
      <ConfirmDialog {...componentProps} id={props.name} />
    ),
    [props.name],
  );

  return {
    closeConfirmDialog,
    DialogComponent,
    dialogOpen: showDialog.get(props.name) ?? false,
    toggleConfirmDialog,
  };
};
